import React, { useContext, useEffect } from 'react';

import { hot } from 'react-hot-loader/root';

import { TrackEventContext } from 'common/containers/EventContainer';
import StickerMuleLandingHelmet from 'common/helmets/StickerMuleLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';
import SitckerMule1 from 'img/landing/stickermule-1.webp';
import SitckerMule2 from 'img/landing/stickermule-2.webp';
import StickerMuleWordmark from 'img/landing/stickermule-wordmark.webp';
import Viamonte from 'img/landing/viamonte.webp';

import 'css/components/www/_LandingCustomersPage.scss';

const StickerMuleLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Sticker Mule Case Study');
  }, [trackEvent]);

  return (
    <div className="landingStickerMule landingCustomersPage">
      <StickerMuleLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Sticker Mule uses Canny to reveal big wins" />
          <div className="headerImage">
            <div className="nametag">
              <div className="name">Viamonte Sherman</div>
              <div className="title">VP of Technology at Sticker Mule</div>
            </div>
            <LazyLoadedImage loading="eager" src={Viamonte} alt="Viamonte Sherman" />
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>
              If you’re looking for a company that does pretty much everything under the sun,
              Sticker Mule is&nbsp;it.
            </p>
            <p>
              They make a variety of customizable products—stickers, buttons, magnets, and so on—but
              they’re also a software company with several design tools. And, they make hot sauce.
              No&nbsp;joke.
            </p>
            <p>
              Viamonte Sherman is the VP of Technology at Sticker Mule, and he keeps the place
              running from a software&nbsp;perspective.
            </p>
            <p>
              Besides managing the software team, he also makes sure that every other department is
              also looped in, and everything is&nbsp;aligned.
            </p>
            <p className="quote">
              &ldquo;If you think of a watch and all the gears it takes to make it work, my team is
              the gear that touches pretty much every other one—sales, marketing, CS, manufacturing,
              and so&nbsp;on.&rdquo;
            </p>
            <h2 className="sectionTitle">Challenge</h2>
            <p>
              Before Canny, Sticker Mule’s feedback channels were a combination of Slack messages,
              emails, and phone conversations. This caused important things to slip through
              the&nbsp;cracks.
            </p>
            <p>
              In business, it’s important to move on things—especially problems—fast. But problems
              can’t be fixed if you don’t know about&nbsp;them.
            </p>
            <p className="quote">
              &ldquo;Sometimes the message just wouldn’t get across, and by the time we got to it,
              it was too&nbsp;late.&rdquo;
            </p>
            <p>
              Even though the team made a good effort to consolidate requests and issues into one
              place, it was proving to be more and more time-consuming and complicated as time
              went&nbsp;by.
            </p>
            <p>
              Documentation quickly became stale and irrelevant or unclear due to missing
              context/expertise. Communication was becoming a hodgepodge of different channels, and
              the upkeep was a&nbsp;challenge.
            </p>
            <p>
              Besides making sure that urgent things don’t go unnoticed, Sticker Mule also wanted to
              provide a better user experience for the customers who wanted to give&nbsp;feedback.
            </p>
            <p>The team was looking for a tool that could fill the following&nbsp;requirements:</p>
            <ul>
              <li>
                A single place for all requests, with the ability to sort through and prioritize
                feedback quickly and&nbsp;efficiently
              </li>
              <li>
                An easy-to-use platform with a great user experience, to make leaving feedback and
                staying in the loop easy for the&nbsp;customer
              </li>
            </ul>
            <h2 className="sectionTitle">Solution</h2>
            <p>
              It was Sticker Mule’s CEO who originally discovered Canny, and figured it might be
              just what the doctor ordered. From there, setup&nbsp;began.
            </p>
            <p>Viamonte was pretty impressed with the ease of the&nbsp;process.</p>
            <p className="quote">
              &ldquo;I remember thinking: ‘Wait, that’s it?!’ We were ready to let people start
              using it practically the next&nbsp;day.&rdquo;
            </p>
            <p>
              Since Sticker Mule had never used a tool like Canny before, they weren’t sure if and
              how users would engage with it. Luckily, it took off right&nbsp;away.
            </p>
            <LazyLoadedImage className="border" src={SitckerMule1} />
            <p>
              Viamonte attributes the high engagement rate to the ease of use that
              Canny&nbsp;provides.
            </p>
            <p className="quote">
              &ldquo;It’s not difficult to use at all. The interface is great. It’s not a big ask to
              tell someone to take a look. It’s two minutes of your&nbsp;life.&rdquo;
            </p>
            <p>
              Besides feedback from external users, Sticker Mule also has a private Canny board for
              internal&nbsp;conversations.
            </p>
            <p>
              Canny has made combing through feedback easier than ever, and made sure that nothing
              is slipping through the cracks or going&nbsp;unnoticed.
            </p>
            <p>Important things bubble to the top, and can be immediately&nbsp;prioritized.</p>
            <p className="quote">
              &ldquo;Customers love the communication from us as well, and knowing that their
              feedback isn’t just going into a black&nbsp;hole.&rdquo;
            </p>
            <h2 className="sectionTitle">Results</h2>
            <p>
              One of the clear benefits of Canny has been the ease of collaboration and
              communication that it provides for both the team and their&nbsp;customers.
            </p>
            <p>
              Practically everyone at Sticker Mule uses Canny, from marketing, to the CEO, to the
              product&nbsp;team.
            </p>
            <p>
              Everyone is aware of everything that’s going on, and visibility has increased
              tremendously for both the team and the&nbsp;users.
            </p>
            <p>
              When it comes to the internal Canny board, it has helped save time and energy when
              planning team meetings and having internal&nbsp;conversations.
            </p>
            <p className="quote">
              &ldquo;We have a weekly meeting where we all get together. Before, deciding on the
              topics for this was almost like pulling teeth. Now, we can all just suggest them,
              and&nbsp;vote.&rdquo;
            </p>
            <p>
              Senior management also engages with the internal board often—it’s proven to be a great
              source of suggestions for the&nbsp;company.
            </p>
            <p className="quote">
              &ldquo;We can take a look and say, ‘This is an issue or an opportunity. Let’s make
              a&nbsp;change.&rdquo;
            </p>
            <p>
              As for the public board, the most important benefit has been unveiling big wins that
              weren’t on anyone’s radar&nbsp;before.
            </p>
            <p>
              The teams at Sticker Mule track a bunch of KPIs that are all connected to revenue
              growth and&nbsp;productivity.
            </p>
            <p className="quote">
              &ldquo;I would say Canny has uncovered some big things that have added
              to&nbsp;both.&rdquo;
            </p>
            <p>
              As an example, Viamonte recalls when the external Canny board helped them realize just
              how excited people were about a very specific&nbsp;product.
            </p>
            <p>
              They had heard that people would like holographic stickers, but weren’t sure what the
              demand really was. When they posted the idea on Canny, it&nbsp;exploded.
            </p>
            <LazyLoadedImage className="border" src={SitckerMule2} />
            <p className="quote">
              &ldquo;We had no idea how juiced people were about them. And now we
              sell&nbsp;them.&rdquo;
            </p>
            <p>
              Ultimately, Canny has helped the Sticker Mule team focus on what’s really important
              for their&nbsp;business.
            </p>
            <p className="quote">
              &ldquo;Things bubbling up and becoming clear like that saves us money and time. And,
              how easy it all is saves the customer and team trouble,&nbsp;too.&rdquo;
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="StickerMule Wordmark"
              className="stickerMuleWordmark"
              src={StickerMuleWordmark}
            />
            The Internet’s most kick-ass printing&nbsp;company.
            <a href="https://www.stickermule.com/" rel="noopener" target="_blank">
              stickermule.com
            </a>
            <div className="stat">
              <UppercaseHeader>Customers</UppercaseHeader>
              <p>A lot</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Location</UppercaseHeader>
              <p>Global</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Using Canny since</UppercaseHeader>
              <p>2018</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>46,000+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default hot(StickerMuleLanding);
